import React, { useEffect, useState, useRef } from 'react'
import { MapContainer, TileLayer, FeatureGroup, LayerGroup, useMap } from 'react-leaflet'
import { EditControl } from 'react-leaflet-draw'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import L from 'leaflet'
import { fetchWithAuth } from '../utils/api'

// Import Leaflet CSS again to ensure it's loaded
import 'leaflet/dist/leaflet.css'

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl

// Configure Leaflet icons
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
})

// Fix for leaflet-draw icons
if (typeof window !== 'undefined') {
  // Set the path for Leaflet images
  L.Icon.Default.imagePath = '/images/'
  
  // Create a custom icon for editing vertices (half size)
  const customIcon = L.icon({
    iconUrl: '/images/marker-icon.png',
    iconRetinaUrl: '/images/marker-icon-2x.png',
    shadowUrl: '/images/marker-shadow.png',
    iconSize: [13, 21],        // Half of original [25, 41]
    iconAnchor: [6, 21],       // Half of original [12, 41] 
    popupAnchor: [1, -17],     // Adjusted for smaller size
    shadowSize: [21, 21]      // Half of original [41, 41]
  })
  
  // Apply to default icon
  L.Marker.prototype.options.icon = customIcon
}

interface PolygonEditorProps {
  initialPolygon?: number[][] // Polygon coordinates as [lat, lng] pairs (Swedish format: [59, 16])
  onPolygonChange: (coordinates: number[][] | null) => void
  onClose: () => void
  title?: string
  siteId?: number // For showing associated areas when editing site boundaries
}

// Component to handle areas visualization within the map context
const AreasVisualizer: React.FC<{
  areas: Array<{ id: number; name: string; polygon_coords: number[][] | null }>
  areasLayerRef: React.RefObject<L.LayerGroup>
}> = ({ areas, areasLayerRef }) => {
  const map = useMap()

  useEffect(() => {
    if (!areasLayerRef.current || areas.length === 0) return

    // Clear existing area layers
    areasLayerRef.current.clearLayers()

    areas.forEach((area) => {
      if (!area.polygon_coords || area.polygon_coords.length === 0) return

      // Database stores [lat, lng] - use directly for Leaflet
      const leafletCoords = area.polygon_coords.map((coord: number[]) => [coord[0], coord[1]] as [number, number])
      
      // Create polygon for area
      const areaPolygon = L.polygon(leafletCoords, {
        color: '#ff7849',
        weight: 2,
        opacity: 0.8,
        fillColor: '#ff7849',
        fillOpacity: 0.15,
        interactive: false // Make it non-interactive so it doesn't interfere with editing
      })

      // Calculate center point for label
      const bounds = areaPolygon.getBounds()
      const center = bounds.getCenter()

      // Create label for area
      const areaLabel = L.marker(center, {
        icon: L.divIcon({
          className: 'area-label-container',
          html: `<div class="area-label">${area.name}</div>`,
          iconSize: [100, 20],
          iconAnchor: [50, 10]
        }),
        interactive: false
      })

      // Add both polygon and label to areas layer
      if (areasLayerRef.current) {
        areasLayerRef.current.addLayer(areaPolygon)
        areasLayerRef.current.addLayer(areaLabel)
      }
    })
  }, [areas, areasLayerRef, map])

  return null
}

// Component to handle site visualization within the map context
const SiteVisualizer: React.FC<{
  site: { id: number; name: string; polygon_coords: number[][] | null } | null
  siteLayerRef: React.RefObject<L.LayerGroup>
}> = React.memo(({ site, siteLayerRef }) => {
  useEffect(() => {
    if (!siteLayerRef.current) return
    if (!site) return
    if (!site.polygon_coords || site.polygon_coords.length === 0) return

    // Clear existing site layers
    siteLayerRef.current.clearLayers()

    // Database stores [lat, lng] - use directly for Leaflet
    const leafletCoords = site.polygon_coords.map((coord: number[]) => [coord[0], coord[1]] as [number, number])
    
    // Create polygon for site boundary (more subtle than areas)
    const sitePolygon = L.polygon(leafletCoords, {
      color: '#3498db',
      weight: 3,
      opacity: 0.6,
      fillColor: '#3498db',
      fillOpacity: 0.05,
      interactive: false // Make it non-interactive so it doesn't interfere with editing
    })

    // Calculate center point for label
    const bounds = sitePolygon.getBounds()
    const center = bounds.getCenter()

    // Create label for site
    const siteLabel = L.marker(center, {
      icon: L.divIcon({
        className: 'site-label-container',
        html: `<div class="site-label">${site.name}</div>`,
        iconSize: [120, 25],
        iconAnchor: [60, 12]
      }),
      interactive: false
    })

    // Add both polygon and label to site layer
    siteLayerRef.current.addLayer(sitePolygon)
    siteLayerRef.current.addLayer(siteLabel)
  }, [site, siteLayerRef])

  return null
})

// Component to handle initial polygon loading within the map context
const MapInitializer: React.FC<{
  initialPolygon?: number[][]
  featureGroupRef: React.RefObject<L.FeatureGroup>
  onCoordinatesUpdate: (coordinates: string[]) => void
  onEditingStateChange: (editing: boolean) => void
  hasCenteredRef: React.MutableRefObject<boolean>
}> = ({ initialPolygon, featureGroupRef, onCoordinatesUpdate, onEditingStateChange, hasCenteredRef }) => {
  const map = useMap()

  const formatCoordinates = (coordinates: L.LatLng[]) => {
    return coordinates.map((latlng, index) => 
      `Point ${index + 1}: ${latlng.lat.toFixed(6)}°N, ${latlng.lng.toFixed(6)}°E`
    )
  }

  useEffect(() => {
    const loadInitialPolygon = () => {
                        
      if (initialPolygon && initialPolygon.length > 0 && featureGroupRef.current) {
                
        // Clear any existing layers first
        featureGroupRef.current.clearLayers()
        
        // Database stores [lat, lng] - use directly for Leaflet
        const leafletCoords = initialPolygon.map(coord => [coord[0], coord[1]] as [number, number])
        const polygon = L.polygon(leafletCoords)
        featureGroupRef.current.addLayer(polygon)
        
        // Only center map on the polygon once
        if (!hasCenteredRef.current) {
          const bounds = polygon.getBounds()
          const center = bounds.getCenter()
                    map.setView([center.lat, center.lng], 14) // Set specific zoom level 14
          hasCenteredRef.current = true
                  } else {
                  }
        
        // Set initial coordinate display
        const latlngs = leafletCoords.map(coord => L.latLng(coord[0], coord[1]))
        onCoordinatesUpdate(formatCoordinates(latlngs))
        onEditingStateChange(true)
        
              }
    }

    // Use a small delay to ensure the map is fully rendered
    const timer = setTimeout(loadInitialPolygon, 100)
    return () => clearTimeout(timer)
  }, [initialPolygon, featureGroupRef, onCoordinatesUpdate, onEditingStateChange])

  return null
}

const PolygonEditor: React.FC<PolygonEditorProps> = ({
  initialPolygon,
  onPolygonChange,
  onClose,
  title = "Edit Area Boundary",
  siteId
}) => {
  const [center] = useState<[number, number]>([62.0, 15.0]) // Center of Sweden as default
  const [zoom] = useState(14) // Set a reasonable initial zoom level
  const [mapError, setMapError] = useState<string | null>(null)
  const [currentCoordinates, setCurrentCoordinates] = useState<string[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [locatingUser, setLocatingUser] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)
  const [showCoordinates, setShowCoordinates] = useState(false)
  const [siteAreas, setSiteAreas] = useState<Array<{ id: number; name: string; polygon_coords: number[][] | null }>>([])
  const [siteData, setSiteData] = useState<{ id: number; name: string; polygon_coords: number[][] | null } | null>(null)
  const featureGroupRef = useRef<L.FeatureGroup>(null)
  const areasLayerRef = useRef<L.LayerGroup>(null)
  const siteLayerRef = useRef<L.LayerGroup>(null)
  const hasCenteredOnSiteRef = useRef(false)

  // Reset centering ref on mount to ensure fresh start
  useEffect(() => {
    hasCenteredOnSiteRef.current = false
    return () => {}
  }, [])

  // Fetch areas and site data if siteId is provided
  useEffect(() => {
    if (siteId) {
      fetchSiteAreas()
      fetchSiteData()
    }
  }, [siteId])

  // Center map on site when site data is first loaded (only if no initial polygon)
  useEffect(() => {
    if (siteData && siteData.polygon_coords && !hasCenteredOnSiteRef.current && siteLayerRef.current && !initialPolygon) {
      const timer = setTimeout(() => {
        const leafletCoords = siteData.polygon_coords!.map((coord: number[]) => [coord[0], coord[1]] as [number, number])
        const polygon = L.polygon(leafletCoords)
        const bounds = polygon.getBounds()
        
        const map = (siteLayerRef.current as any)?._map
        if (map) {
          const center = bounds.getCenter()
          map.setView([center.lat, center.lng], 14) // Set specific zoom level 14
          hasCenteredOnSiteRef.current = true
        }
      }, 200)
      
      return () => clearTimeout(timer)
    }
  }, [siteData, initialPolygon])


  const fetchSiteAreas = async () => {
    if (!siteId) return
    
    try {
      const response = await fetchWithAuth(`/api/admin/sites/${siteId}/areas`)
      
      if (response.ok) {
        const areas = await response.json()
        setSiteAreas(areas.filter((area: any) => area.polygon_coords)) // Only areas with boundaries
              }
    } catch (error) {
      console.warn('Failed to fetch site areas:', error)
    }
  }

  const fetchSiteData = async () => {
    if (!siteId) return
    
    try {
      const response = await fetchWithAuth(`/api/admin/sites/${siteId}`)
      
      if (response.ok) {
        const site = await response.json()
        setSiteData(site)
              }
    } catch (error) {
      console.warn('Failed to fetch site data:', error)
    }
  }

  // Callback functions for MapInitializer
  const handleCoordinatesUpdate = (coordinates: string[]) => {
    setCurrentCoordinates(coordinates)
  }

  const handleEditingStateChange = (editing: boolean) => {
    setIsEditing(editing)
  }

  const formatCoordinates = (coordinates: L.LatLng[]) => {
    return coordinates.map((latlng, index) => 
      `Point ${index + 1}: ${latlng.lat.toFixed(6)}°N, ${latlng.lng.toFixed(6)}°E`
    )
  }

  const handleCreated = (e: any) => {
    const { layer } = e
    if (layer instanceof L.Polygon) {
      // Clear existing polygons (only allow one)
      if (featureGroupRef.current) {
        featureGroupRef.current.clearLayers()
        featureGroupRef.current.addLayer(layer)
      }
      
      const coordinates = layer.getLatLngs() as L.LatLng[][]
      // Keep [lat, lng] format for storage (database expects this)
      const polygonCoordinates = coordinates[0].map((latlng: L.LatLng) => [latlng.lat, latlng.lng])
      
      // Update coordinate display
      setCurrentCoordinates(formatCoordinates(coordinates[0]))
      setIsEditing(true)
      
      onPolygonChange(polygonCoordinates)
    }
  }

  const handleEdited = (e: any) => {
    const layers = e.layers
    layers.eachLayer((layer: L.Polygon) => {
      const coordinates = layer.getLatLngs() as L.LatLng[][]
      // Keep [lat, lng] format for storage (database expects this)
      const polygonCoordinates = coordinates[0].map((latlng: L.LatLng) => [latlng.lat, latlng.lng])
      
      // Update coordinate display
      setCurrentCoordinates(formatCoordinates(coordinates[0]))
      setIsEditing(true)
      
      onPolygonChange(polygonCoordinates)
    })
  }

  const handleDeleted = () => {
    setCurrentCoordinates([])
    setIsEditing(false)
    onPolygonChange(null)
  }

  const centerMapOnUserLocation = () => {
    setLocatingUser(true)
    
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          const featureGroup = featureGroupRef.current
          if (featureGroup) {
            const map = (featureGroup as any)._map
            if (map) {
              map.setView([latitude, longitude], 15) // Zoom level 15 for detailed view
            }
          }
          setLocatingUser(false)
        },
        (error) => {
          console.error('Geolocation error:', error)
          setMapError('Unable to get your location. Please check location permissions.')
          setLocatingUser(false)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      )
    } else {
      setMapError('Geolocation is not supported by your browser.')
      setLocatingUser(false)
    }
  }

  return (
    <div className="polygon-editor-overlay">
      <div className="polygon-editor-container">
        <div className="polygon-editor-header">
          <div>
            <h3>{title}</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
              <button 
                className="btn" 
                onClick={centerMapOnUserLocation}
                disabled={locatingUser}
                style={{ 
                  fontSize: '0.8rem', 
                  padding: '0.3rem 0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}
              >
                {locatingUser ? '📍 Locating...' : '📍 Center on Me'}
              </button>
              <button 
                className="btn" 
                onClick={() => setShowInstructions(!showInstructions)}
                style={{ 
                  fontSize: '0.8rem', 
                  padding: '0.3rem 0.8rem'
                }}
              >
                {showInstructions ? '📖 Hide Help' : '❓ Show Help'}
              </button>
              {isEditing && (
                <button 
                  className="btn" 
                  onClick={() => setShowCoordinates(!showCoordinates)}
                  style={{ 
                    fontSize: '0.8rem', 
                    padding: '0.3rem 0.8rem'
                  }}
                >
                  {showCoordinates ? '📍 Hide GPS' : '📍 Show GPS'}
                </button>
              )}
              <button 
                className="btn btn-danger" 
                onClick={() => {
                  // Clear the polygon layers
                  if (featureGroupRef.current) {
                    featureGroupRef.current.clearLayers()
                  }
                  // Update local state
                  setCurrentCoordinates([])
                  setIsEditing(false)
                  // Notify parent component
                  onPolygonChange(null)
                }}
                style={{ 
                  fontSize: '0.8rem', 
                  padding: '0.3rem 0.8rem'
                }}
              >
                🗑️ Clear
              </button>
              <button 
                className="btn btn-success" 
                onClick={onClose}
                style={{ 
                  fontSize: '0.8rem', 
                  padding: '0.3rem 0.8rem'
                }}
              >
                💾 Save Area Boundary
              </button>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#666',
              padding: '0.25rem',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2rem',
              height: '2rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0'
              e.currentTarget.style.color = '#333'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'none'
              e.currentTarget.style.color = '#666'
            }}
          >
            ×
          </button>
        </div>
        
        {showInstructions && (
          <div className="polygon-editor-instructions">
            <p style={{ margin: '0 0 0.5rem 0' }}>🗺️ How to use the polygon editor:</p>
            <ul>
              <li>Click the polygon tool (□) to start drawing</li>
              <li>Click points on the map to create boundaries</li>
              <li>Double-click to complete the polygon</li>
              <li>Click on vertices to edit existing boundaries</li>
              <li>Use the "Clear" button above to remove boundaries</li>
            </ul>
          </div>
        )}
        
        {mapError && (
          <div style={{ color: 'red', padding: '1rem 1.5rem', backgroundColor: '#ffebee', borderBottom: '1px solid #eee' }}>
            ⚠️ {mapError}
          </div>
        )}

        <div className="map-container">
          <MapContainer
            center={center}
            zoom={zoom}
            style={{ 
              height: '100%', 
              width: '100%', 
              minHeight: 'max(800px, 75vh)' 
            }}
            className="leaflet-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              subdomains={['a', 'b', 'c']}
              maxZoom={18}
              eventHandlers={{
                tileerror: () => {
                  console.warn('Tile loading error detected')
                  setMapError('Map tiles may not load properly. The drawing tools will still work.')
                },
                tileload: () => {
                  if (mapError) setMapError(null)
                }
              }}
            />
            <LayerGroup ref={siteLayerRef}>
              <SiteVisualizer
                site={siteData}
                siteLayerRef={siteLayerRef}
              />
            </LayerGroup>
            <LayerGroup ref={areasLayerRef}>
              <AreasVisualizer
                areas={siteAreas}
                areasLayerRef={areasLayerRef}
              />
            </LayerGroup>
            <FeatureGroup ref={featureGroupRef}>
              <MapInitializer
                initialPolygon={initialPolygon}
                featureGroupRef={featureGroupRef}
                onCoordinatesUpdate={handleCoordinatesUpdate}
                onEditingStateChange={handleEditingStateChange}
                hasCenteredRef={hasCenteredOnSiteRef}
              />
              <EditControl
                position="topright"
                onCreated={handleCreated}
                onEdited={handleEdited}
                onDeleted={handleDeleted}
                draw={{
                  rectangle: false,
                  circle: false,
                  circlemarker: false,
                  marker: false,
                  polyline: false,
                  polygon: {
                    allowIntersection: false,
                    drawError: {
                      color: '#e1e100',
                      message: '<strong>Oh snap!</strong> You can\'t draw that!'
                    },
                    shapeOptions: {
                      color: '#667eea',
                      weight: 3,
                      opacity: 0.8,
                      fillColor: '#667eea',
                      fillOpacity: 0.2
                    }
                  }
                }}
                edit={{
                  featureGroup: featureGroupRef.current,
                  edit: false,    // Disable the edit button
                  remove: false   // Disable the remove button
                }}
              />
            </FeatureGroup>
          </MapContainer>
        </div>

        {isEditing && currentCoordinates.length > 0 && showCoordinates && (
          <div className="coordinate-display">
            <div className="coordinate-header">
              <h4>📍 GPS Coordinates</h4>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                {currentCoordinates.length} points defined
              </div>
            </div>
            <div className="coordinate-list">
              {currentCoordinates.map((coord, index) => (
                <div key={index} className="coordinate-item">
                  {coord}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default PolygonEditor
